import React, { useMemo } from "react";
import styled from "styled-components";

/**
 * assetNames は Object ({ assetType: displayName }) または Map の両方に対応
 */
const getAssetDisplayName = (assetNames, assetType) => {
  if (!assetNames) return assetType;
  if (typeof assetNames.get === "function")
    return assetNames.get(assetType) || assetType;
  return assetNames[assetType] || assetType;
};

/**
 * AIが提案した入力値の確認UIコンポーネント
 * actions を資産タイプ別・ポートフォリオ別にグループ化して表示する
 */
const ActionProposal = ({ actions, onApply, onCancel, assetNames }) => {
  // ポートフォリオ名 → 資産タイプ名 → アクション一覧 の形にグループ化
  const grouped = useMemo(() => {
    const map = new Map();
    actions.forEach((action) => {
      if (action.type !== "updateAmount") return;
      const { portfolio, asset_type, product, amount_in_million } = action;
      if (!map.has(portfolio)) map.set(portfolio, new Map());
      const portMap = map.get(portfolio);
      if (!portMap.has(asset_type)) portMap.set(asset_type, []);
      portMap.get(asset_type).push({ product, amount_in_million });
    });
    return map;
  }, [actions]);

  // ポートフォリオ別合計（万円）
  const portfolioTotals = useMemo(() => {
    const totals = {};
    actions.forEach(({ portfolio, amount_in_million }) => {
      totals[portfolio] = (totals[portfolio] || 0) + (amount_in_million || 0);
    });
    return totals;
  }, [actions]);

  const formatAmount = (amountInMillion) => {
    if (amountInMillion == null || amountInMillion === 0) return "0 万円";
    if (amountInMillion >= 10000) {
      // 1億以上は億円表示
      const oku = amountInMillion / 100;
      return `${oku.toLocaleString()} 億円`;
    }
    return `${amountInMillion.toLocaleString()} 万円`;
  };

  if (actions.length === 0) return null;

  return (
    <ProposalContainer>
      <ProposalHeader>
        <LightBulb>💡</LightBulb>
        <HeaderText>以下の入力値を提案します</HeaderText>
      </ProposalHeader>

      <ProposalBody>
        {Array.from(grouped.entries()).map(([portName, assetMap]) => (
          <PortfolioSection key={portName}>
            <PortfolioName>
              📁 {portName}
              <TotalBadge>
                合計: {formatAmount(portfolioTotals[portName])}
              </TotalBadge>
            </PortfolioName>

            {Array.from(assetMap.entries()).map(([assetType, products]) => (
              <AssetSection key={assetType}>
                <AssetTypeName>
                  {getAssetDisplayName(assetNames, assetType)}
                </AssetTypeName>
                {products.map(({ product, amount_in_million }) => (
                  <ProductRow key={product}>
                    <ProductName>{product}</ProductName>
                    <Arrow>→</Arrow>
                    <Amount>{formatAmount(amount_in_million)}</Amount>
                  </ProductRow>
                ))}
              </AssetSection>
            ))}
          </PortfolioSection>
        ))}
      </ProposalBody>

      <ButtonRow>
        <ApplyButton onClick={onApply}>✅ 適用する</ApplyButton>
        <CancelButton onClick={onCancel}>✏️ 修正する</CancelButton>
      </ButtonRow>
    </ProposalContainer>
  );
};

const ProposalContainer = styled.div`
  background-color: #f0f9ff;
  border: 1px solid #a7e0f7;
  border-radius: 8px;
  margin: 8px 0;
  overflow: hidden;
`;

const ProposalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background-color: #e0f2fe;
  border-bottom: 1px solid #a7e0f7;
`;

const LightBulb = styled.span`
  font-size: 14px;
`;

const HeaderText = styled.span`
  font-size: 11px;
  font-weight: bold;
  color: #192e55;
`;

const ProposalBody = styled.div`
  padding: 10px 12px;
  max-height: 240px;
  overflow-y: auto;
`;

const PortfolioSection = styled.div`
  margin-bottom: 10px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const PortfolioName = styled.div`
  font-size: 11px;
  font-weight: bold;
  color: #192e55;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
`;

const TotalBadge = styled.span`
  font-size: 10px;
  font-weight: normal;
  color: #2397ce;
  background-color: #e0f2fe;
  padding: 1px 6px;
  border-radius: 10px;
  border: 1px solid #a7e0f7;
`;

const AssetSection = styled.div`
  margin-left: 16px;
  margin-bottom: 6px;
`;

const AssetTypeName = styled.div`
  font-size: 10px;
  font-weight: bold;
  color: #546e7a;
  margin-bottom: 3px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ProductRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 0;
  margin-left: 8px;
`;

const ProductName = styled.span`
  font-size: 11px;
  color: #192e55;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Arrow = styled.span`
  font-size: 10px;
  color: #90a4ae;
  flex-shrink: 0;
`;

const Amount = styled.span`
  font-size: 11px;
  font-weight: bold;
  color: #1565c0;
  flex-shrink: 0;
  min-width: 80px;
  text-align: right;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  padding: 8px 12px;
  background-color: #e0f2fe;
  border-top: 1px solid #a7e0f7;
`;

const BaseButton = styled.button`
  flex: 1;
  padding: 7px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: bold;
  cursor: pointer;
  border: none;
  transition: background-color 0.15s ease;

  &:active {
    transform: scale(0.98);
  }
`;

const ApplyButton = styled(BaseButton)`
  background-color: #2397ce;
  color: white;

  &:hover {
    background-color: #1a7aaa;
  }
`;

const CancelButton = styled(BaseButton)`
  background-color: white;
  color: #192e55;
  border: 1px solid #c0d8e8;

  &:hover {
    background-color: #f5f5f5;
  }
`;

export default ActionProposal;
